import { AxiosError } from "axios";
// eslint-disable-next-line no-restricted-imports
import i18n from "i18next";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { switchOrg } from "api/api-server-actions";
import { LoadingScreen } from "components/LoadingScreen";
import { FullSectionType } from "components/server-types";
import { ToastKeys } from "i18n/i18next.js";
import { ListItem } from "mds/components/ListItem";
import { BookCourseIcon } from "mds/icons";
import { storeApi, useAppSelector } from "store/index";
import { selectCoursesThatCanBeAuthored, selectTerms } from "store/selectors";
import { useListSelector } from "store/store-hooks";
import {
  toastLocalizedOperationCatcher,
  toastLocalizedOperationError,
  toastSuccessMessage,
} from "utils/alerts";
import { getOrgIdFromCookie } from "utils/auth";
import { rollbarAndLogError } from "utils/logger";
import { useNavigate, useQueryParamsManager } from "utils/routing";

interface CoursesByTerm {
  [termId: string]: FullSectionType[];
}

interface CourseListItemProps {
  course: FullSectionType;
  isLinked: boolean;
  onSelectCourse: (courseId: string) => void;
}

const CourseListItem: React.FC<CourseListItemProps> = ({ course, isLinked, onSelectCourse }) => (
  <ListItem
    className="flex justify-between"
    // @ts-expect-error disabled is not typed
    disabled={isLinked}
    label={course.full_title}
    onClick={isLinked ? undefined : () => onSelectCourse(course.id)}
  >
    <div className="flex items-center gap-2">
      <BookCourseIcon />
      <span className={isLinked ? "text-black-tint-55" : ""}>{course.full_title}</span>
      {isLinked && (
        <span className="ml-2 text-black-tint-55">(Linked to course #{course.lms_section_id})</span>
      )}
    </div>
  </ListItem>
);

export default function Layout() {
  const { getQueryParam } = useQueryParamsManager();
  const { t } = useTranslation();
  const coursesThatCanBeAuthored = useListSelector(selectCoursesThatCanBeAuthored);
  const unlinkedCoursesThatCanBeAuthored = useMemo(
    () => coursesThatCanBeAuthored.filter((course) => course.lms_section_id === null),
    [coursesThatCanBeAuthored],
  );
  const terms = useAppSelector(selectTerms);
  const navigate = useNavigate();

  const orgId = getQueryParam("orgId");
  const orgIdFromCookie = getOrgIdFromCookie();

  useEffect(() => {
    const maybeSwitchOrg = async () => {
      if (orgId && orgId !== orgIdFromCookie) {
        try {
          await switchOrg(orgId);
          window.location.reload();
        } catch (error) {
          rollbarAndLogError("Failed to auto-switch org", error);
        }
      }
    };
    maybeSwitchOrg();
  }, [orgIdFromCookie, orgId]);

  useEffect(() => {
    storeApi.course_users.list();
    storeApi.course_codes.list();
    storeApi.terms.list();
  }, []);

  const coursesByTerm = useMemo(() => {
    return unlinkedCoursesThatCanBeAuthored.reduce((acc: CoursesByTerm, course) => {
      const termId = course.term_id;
      return {
        ...acc,
        [termId]: [...(acc[termId] || []), course],
      };
    }, {} as CoursesByTerm);
  }, [unlinkedCoursesThatCanBeAuthored]);

  if (unlinkedCoursesThatCanBeAuthored.length === 0) {
    return (
      <LoadingScreen>
        <div className="flex flex-col gap-4 text-center">
          <div>{t("link.course_not_linked_1")}</div>
          <div>{t("link.course_not_linked_2")}</div>
        </div>
      </LoadingScreen>
    );
  }

  const lmsSectionId = getQueryParam("lmsSectionId");

  if (!lmsSectionId) {
    return <LoadingScreen>{t("link.no_courses_available")}</LoadingScreen>;
  }

  const onSelectCourse = (courseId: string) => {
    storeApi.courses
      .partial_update(
        {
          id: courseId,
          lms_section_id: lmsSectionId,
        },
        { skipToast: true },
      )
      .then(() => {
        toastSuccessMessage(t("success.toasts.link_course_started_success"));
        navigate(`/redirect?courseId=${courseId}`);
      })
      .catch((error: AxiosError) => {
        // TODO: make a generic function for this
        const data = error.response?.data as Record<string, string>;
        if (data && "error" in data) {
          if (!i18n.exists(`error.toasts.${data.error}`)) {
            // If it doesn't exist, default to a generic error
            toastLocalizedOperationError("unknown");
          } else toastLocalizedOperationError(data.error as ToastKeys);
        } else {
          toastLocalizedOperationCatcher("link_lms_failure")(error);
        }
      });
  };

  return (
    <div>
      {Object.entries(coursesByTerm).map(([termId, termCourses]) => {
        const term = terms.find((termItem) => termItem.id === termId);
        const termTitle = term ? term.title : "Unassigned";
        return (
          <div key={termId}>
            <h2 className="mb-2 text-lg font-bold">{termTitle}</h2>
            {termCourses.map((course) => (
              <CourseListItem
                course={course}
                isLinked={course.lms_section_id !== null}
                key={course.id}
                onSelectCourse={onSelectCourse}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
}
